import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exerciseImageUrl } from "@/components/ExerciseWorksheet";
import { activePrescriptionRows } from "@/lib/prescriptionDelivery";
import { toast } from "sonner";
import { Activity, AlertOctagon, ChevronRight, ExternalLink, MessageSquare, Sparkles, Star, Trophy } from "lucide-react";

const GOOGLE_REVIEW_URL =
  "https://www.google.com/search?q=bexley+road+physiotherapy&oq=bexley+road+physiotherapy&gs_lcrp=EgZjaHJvbWUqCggAEAAY4wIYgAQyCggAEAAY4wIYgAQyEAgBEC4YrwEYxwEYgAQYjgUyCAgCEAAYFhgeMgoIAxAAGAoYFhgeMgoIBBAAGIAEGKIEMgcIBRAAGO8FMgoIBhAAGKIEGIkF0gEINDk3NWowajeoAgCwAgA&sourceid=chrome&ie=UTF-8#lrd=0x6b12ba479c67e4a1:0xf218e33f9fc7db30,3,,,,";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoadError("");
    api.get("/dashboard/me").then((r) => setData(r.data)).catch((err) => setLoadError(formatApiError(err.response?.data?.detail) || "Unable to load your dashboard."));
  }, [reloadKey]);

  const displayName = data?.intake?.beta_patient_name?.trim() || user?.name || "Patient";
  const activeHomePlan = data?.active_home_plan;
  const activeProgramPlan = data?.active_program_plan;
  const hasAssignedRehab = !!data?.condition;
  const activePlans = Array.isArray(data?.active_plans)
    ? data.active_plans : [activeHomePlan, activeProgramPlan].filter(Boolean);
  const dashboardExerciseRows = activePlans.length
    ? activePrescriptionRows(activePlans)
    : (data?.condition?.exercises || []).filter((row) => row.is_active !== false);
  const hasAssignedExercises = dashboardExerciseRows.length > 0;

  if (loadError) return <MobileShell><div className="px-6 pt-12" role="alert"><p>{loadError}</p><Button className="mt-4" onClick={() => setReloadKey((key) => key + 1)}>Try again</Button></div></MobileShell>;

  if (!data)
    return (
      <MobileShell>
        <div className="px-6 pt-12 text-slate-500" data-testid="dash-loading">Loading…</div>
      </MobileShell>
    );

  if (!hasAssignedExercises) {
    return (
      <MobileShell>
        <header className="px-6 pt-8 pb-2 flex items-center justify-between" data-testid="dash-header">
          <div>
            <p className="text-xs text-slate-500">Welcome back,</p>
            <h1 className="text-2xl font-bold font-display text-slate-900">{displayName}</h1>
          </div>
        </header>
        <section className="px-6 mt-4">
          <div className="rounded-3xl bg-slate-900 text-white p-5 shadow-sm" data-testid="program-card">
            <p className="text-xs uppercase tracking-[0.2em] text-teal-300">Your program</p>
            <h2 className="mt-2 text-xl font-bold font-display leading-tight">{hasAssignedRehab ? "Your physiotherapist has not assigned exercises yet." : "Your physiotherapist has not assigned your program yet."}</h2>
            <p className="mt-2 text-sm text-slate-300">If you think something is missing, please contact the clinic.</p>
          </div>
        </section>
        {data.show_google_review_prompt ? <GoogleReviewCard /> : null}
        <BetaFeedbackCard />
        <BottomNav showEducation={false} />
      </MobileShell>
    );
  }

  const c = data.condition || {};
  const exercisePlan = activePlans[0];
  const exerciseCount = dashboardExerciseRows.length;
  const programTitle = activePlans.length > 1 ? "Your Exercise Programs" : exercisePlan?.title || c.name || c.program?.["Program Name"] || "Your Exercise Program";
  const todaysExercises = dashboardExerciseRows.slice(0, 5);
  const hasStartedExercises = (data.completed_sessions || 0) > 0;
  const progressCards = [
    data.completed_sessions > 0 ? { icon: <Activity size={17} />, label: "Sessions", value: data.completed_sessions, testid: "stat-sessions" } : null,
    typeof data.confidence_avg === "number" ? { icon: <Sparkles size={17} />, label: "Confidence", value: data.confidence_avg, testid: "stat-confidence" } : null,
  ].filter(Boolean);

  return (
    <MobileShell>
      <header className="px-6 pt-8 pb-2 flex items-center justify-between" data-testid="dash-header">
        <div>
          <p className="text-xs text-slate-500">Welcome back,</p>
          <h1 className="text-2xl font-bold font-display text-slate-900">{displayName}</h1>
        </div>
        <button
          onClick={() => navigate("/profile")}
          data-testid="profile-link"
          className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 grid place-items-center font-semibold"
        >
          {displayName.slice(0, 1).toUpperCase()}
        </button>
      </header>

      <section className="px-6 mt-4">
        <div className="rounded-3xl bg-slate-900 text-white p-5 shadow-sm" data-testid="program-card">
          <p className="text-xs uppercase tracking-[0.2em] text-teal-300">
            {hasAssignedRehab ? "Your Recovery Program" : "Your Exercise Program"}
          </p>
          <h2 className="mt-2 text-xl font-bold font-display leading-tight">
            {programTitle}
          </h2>
          <div className="mt-3 text-sm text-slate-300">
            {exerciseCount} exercise{exerciseCount === 1 ? "" : "s"}
          </div>
          <div className="mt-4">
            <Button
              onClick={() => navigate("/exercises")}
              data-testid="continue-session-btn"
              className="w-full rounded-full bg-teal-500 hover:bg-teal-400 text-white h-11 px-4"
            >
              {hasStartedExercises ? "Continue Exercises" : "Start Exercises"}
            </Button>
          </div>
        </div>
      </section>

      <DashboardExerciseList exercises={todaysExercises} total={exerciseCount} />

      <DashboardPainCheckIn condition={c.name || exercisePlan?.condition || "Exercise Program"} />

      {progressCards.length > 0 && (
        <section className={`px-6 mt-4 grid gap-3 ${progressCards.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid="progress-summary">
          {progressCards.map((card) => <StatCard key={card.testid} {...card} />)}
        </section>
      )}

      <section className="px-6 mt-5 space-y-2">
        <CompactAction
          icon={<AlertOctagon size={18} />}
          title="Flare-up guidance"
          subtitle="What to do if pain spikes"
          onClick={() => navigate("/flare-up")}
          testid="open-flareup-btn"
          tone="amber"
        />
        <CompactAction
          icon={<Trophy size={18} />}
          title="Badges & milestones"
          subtitle={`${data.badges?.length || 0} earned`}
          onClick={() => navigate("/badges")}
          testid="open-badges-btn"
        />
      </section>

      {data.show_google_review_prompt ? <GoogleReviewCard /> : null}

      <BetaFeedbackCard />

      <BottomNav showEducation={hasAssignedRehab} />
    </MobileShell>
  );
}

function DashboardPainCheckIn({ condition }) {
  const [pain, setPain] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const savePain = async () => {
    if (pain === null) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.post("/progress", { condition, pain_score: pain });
      setSaved(true);
      toast.success("Pain check-in saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="px-6 mt-4" data-testid="dashboard-pain-checkin">
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <label htmlFor="dashboard-pain-score" className="text-xs font-bold uppercase tracking-wider text-slate-500">Pain check-in</label>
        <select id="dashboard-pain-score" data-testid="dashboard-pain-score" value={pain ?? ""} onChange={(event) => { setPain(event.target.value === "" ? null : Number(event.target.value)); setSaved(false); }} className="mt-2 w-full rounded-xl border border-slate-200 bg-white p-2 text-sm">
          <option value="">Choose your current pain score</option>
          {Array.from({ length: 11 }, (_, score) => <option key={score} value={score}>{score} / 10</option>)}
        </select>
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{saved ? "Saved" : "How is your pain right now?"}</span>
          <Button type="button" size="sm" onClick={savePain} disabled={saving || pain === null} className="rounded-full bg-slate-900 text-white">
            {saving ? "Saving..." : "Save pain score"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function GoogleReviewCard() {
  return (
    <section className="px-6 mt-6" data-testid="google-review-card">
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-50 text-amber-600">
            <Star size={18} />
          </span>
          <div>
            <h3 className="font-semibold text-slate-900">Google review</h3>
            <p className="mt-1 text-sm leading-5 text-slate-600">Enjoying the app or your care at Bexley Road Physio? You’re welcome to leave us a Google review.</p>
          </div>
        </div>
        <Button asChild variant="outline" className="mt-4 w-full rounded-full bg-white">
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
            Leave a Google review <ExternalLink size={15} />
          </a>
        </Button>
      </div>
    </section>
  );
}

function BetaFeedbackCard() {
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const submitFeedback = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");
    if (!message.trim()) {
      setError("Please add a short message.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        source_page: window.location.pathname,
      });
      setMessage("");
      setSuccess("Thanks, your feedback has been sent.");
      toast.success("Feedback sent");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="px-6 mt-6 pb-8">
      <form onSubmit={submitFeedback} className="rounded-2xl border border-slate-100 bg-white p-4">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 grid place-items-center">
            <MessageSquare size={18} />
          </span>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Beta feedback</h3>
            <p className="mt-1 text-xs text-slate-500">Please use this for app feedback only. Do not include sensitive medical details.</p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 rounded-xl bg-white" data-testid="feedback-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bug">Bug</SelectItem>
              <SelectItem value="confusing">Confusing</SelectItem>
              <SelectItem value="suggestion">Suggestion</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="What should we improve?"
            rows={4}
            className="rounded-xl bg-white"
            data-testid="feedback-message"
          />
          {success ? <div className="rounded-xl bg-teal-50 px-3 py-2 text-sm text-teal-800">{success}</div> : null}
          {error ? <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
          <Button type="submit" disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="feedback-submit">
            {saving ? "Sending..." : "Send feedback"}
          </Button>
        </div>
      </form>
    </section>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-100 p-4">
      <div className="text-teal-600">{icon}</div>
      <div className="mt-2 text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 font-display">{value}</div>
    </div>
  );
}

function DashboardExerciseList({ exercises, total }) {
  if (!exercises.length) return null;
  return (
    <section className="px-6 mt-6" data-testid="assigned-exercise-list">
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Today's assigned exercises</h3>
      {total > exercises.length && <p className="mt-1 text-xs text-slate-600">Showing {exercises.length} of {total}. Open exercises to view the full program.</p>}
      <ul className="mt-2 space-y-2">
        {exercises.map((row, index) => {
          const exercise = row.exercise || row;
          const name = exercise.title || exercise["Exercise / Activity"] || "Exercise";
          const imageUrl = localThumbnailUrl(exerciseImageUrl({ ...exercise, thumbnail_url: row.thumbnail_url || exercise.thumbnail_url }));
          const dosage = exerciseDosage(row, exercise);
          const resistance = resistanceOrEquipment(row, exercise);
          return (
            <li key={row.id || exercise.id || `${name}-${index}`} className="flex gap-3 rounded-2xl border border-slate-100 bg-white p-3" data-testid={`assigned-exercise-${index}`}>
              {imageUrl ? <img src={imageUrl} alt="" className="h-16 w-20 shrink-0 rounded-xl bg-slate-100 object-cover" /> : <div className="grid h-16 w-20 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-400"><Activity size={20} /></div>}
              <div className="min-w-0 py-0.5">
                <div className="text-sm font-semibold text-slate-900">{name}</div>
                <div className="mt-1 text-xs font-medium text-slate-700">{dosage}</div>
                {resistance ? <div className="mt-1 text-xs text-slate-500">{resistance}</div> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function localThumbnailUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin ? parsed.href : "";
  } catch {
    return "";
  }
}

function exerciseDosage(row, exercise) {
  const sets = String(row.sets || exercise.sets || "").trim();
  const reps = String(row.reps || exercise.reps || "").trim();
  const hold = String(row.hold_time || exercise.hold_time || "").trim();
  const parts = [];
  if (sets && reps) parts.push(`${sets} sets × ${reps} reps`);
  else if (sets) parts.push(`${sets} sets`);
  else if (reps) parts.push(`${reps} reps`);
  if (hold) parts.push(`${hold} hold`);
  return parts.join(" · ") || exercise["Suggested Dosage"] || "Dosage as prescribed";
}

function resistanceOrEquipment(row, exercise) {
  const band = String(row.band_colour || row.resistance || "").trim();
  if (band && !/^none$/i.test(band)) return /^(yellow|red|green|blue|black)$/i.test(band) ? `${band} TB` : band;
  const equipment = String(row.equipment || exercise.equipment || "").trim();
  return equipment && !/^(none|n\/a)$/i.test(equipment) ? equipment : "";
}

function ActionTile({ icon, title, subtitle, onClick, testid, tone = "slate" }) {
  const tones = {
    slate: "bg-white",
    teal: "bg-teal-50",
    amber: "bg-amber-50",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.99] transition ${tones[tone]}`}
    >
      <span className="w-9 h-9 rounded-full bg-white text-slate-900 grid place-items-center shadow-sm">{icon}</span>
      <span className="flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        <span className="block text-xs text-slate-500">{subtitle}</span>
      </span>
      <ChevronRight size={18} className="text-slate-400" />
    </button>
  );
}

function CompactAction({ icon, title, subtitle, onClick, testid, tone = "slate" }) {
  const tones = { slate: "bg-white", amber: "bg-amber-50" };
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full text-left rounded-xl border border-slate-100 px-4 py-3 flex items-center gap-3 ${tones[tone]}`}>
      <span className="text-slate-600">{icon}</span>
      <span className="flex-1"><span className="block text-sm font-semibold text-slate-900">{title}</span><span className="block text-xs text-slate-500">{subtitle}</span></span>
      <ChevronRight size={16} className="text-slate-400" />
    </button>
  );
}
